import { useMathTheme } from "@/components/expression/theme/MathThemeProvider";
import { mapColorConfigToColor, mirrorColor } from "@/components/expression/theme/utils";

import type { NodeTypeToTheme, ThemedNodeModel } from "@/components/expression/theme/types";
import type { NodeModel } from "@/models/nodes/types";

export function useNodeTheme<T extends NodeModel>(
  node?: T,
): T extends ThemedNodeModel ? NodeTypeToTheme<T> : undefined;
export function useNodeTheme<T extends ThemedNodeModel>(node?: T) {
  const theme = useMathTheme();

  if (!(node && node.type in theme.nodes)) {
    return undefined;
  }

  if (node.type === "operatorSymbol") {
    const semanticType =
      node.semanticTypes?.find((st) => st === "relation" || st === "punctuation") || "default";
    return theme.nodes.operatorSymbol[semanticType];
  }

  return theme.nodes[node.type];
}

export function useNodeThemeColors(node?: NodeModel) {
  const config = useNodeTheme(node);

  if (!node || !config) {
    return undefined;
  }

  const symbol = mapColorConfigToColor(node, config.symbol);
  const background = mapColorConfigToColor(node, config.background);
  const stroke = mapColorConfigToColor(node, config.stroke);

  return {
    symbol: symbol && {
      light: symbol,
      dark: mirrorColor(symbol),
    },
    background: background && {
      light: background,
      dark: mirrorColor(background),
    },
    stroke: stroke && {
      light: stroke,
      dark: mirrorColor(stroke),
    },
  };
}
